// Token-shape detection + entity tagging for event titles.
//
// A byte-for-byte port of
// `tento/tento-event-classifier/src/entity_normalizer.rs`. Runs BEFORE
// char-n-gram hashing to collapse interchangeable surface forms (names,
// places, times, durations, airports, acronyms) into a small set of
// fixed tags, so the downstream linear model learns abstract patterns
// like `meeting w/ <PERSON> @ <PLACE>` rather than memorizing literals.
//
// Detection rules and the two lexicons (media + airport codes) are
// copied verbatim from the Rust source and MUST stay in sync — see the
// parity test. Lowercasing is ASCII-only on purpose (Rust uses
// `to_ascii_lowercase`); do NOT use `String.prototype.toLowerCase`,
// which is Unicode-aware and would diverge.

import type { Option } from "@tento-lona/sheets";

/**
 * Entity tags. The value of each variant IS its hash key — the fixed
 * string the feature hasher converts to a bucket. Stable across
 * versions; do NOT change without retraining the model.
 */
export const EntityTag = {
  Person: "<PERSON>",
  PersonPossessive: "<PERSON_POSS>",
  Time: "<TIME>",
  TimeRange: "<TIME_RANGE>",
  Duration: "<DURATION>",
  Distance: "<DISTANCE>",
  Num: "<NUM>",
  Airport: "<AIRPORT>",
  Acronym: "<ACRONYM>",
  Place: "<PLACE>",
  Media: "<MEDIA>",
} as const;

export type EntityTag = (typeof EntityTag)[keyof typeof EntityTag];

/**
 * A normalized token: either a plain lowercased word (the model
 * extracts char trigrams + a whole-token hash from it) or an
 * entity-tagged token (the model hashes only the tag's fixed string).
 */
export type Token =
  | { readonly kind: "plain"; readonly text: string }
  | { readonly kind: "entity"; readonly tag: EntityTag };

/** ASCII-only lowercasing — mirrors Rust `str::to_ascii_lowercase`. */
export function asciiLowercase(s: string): string {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    out += c >= 0x41 && c <= 0x5a ? String.fromCharCode(c + 0x20) : s[i];
  }
  return out;
}

// Hand-curated single-token lexicon of streaming services, gaming
// platforms, and recognizable single-word game/show titles. Tagged
// `<MEDIA>` so they share a high-weight feature for the `break` class.
// Verbatim from `entity_normalizer.rs::media_lexicon`.
const MEDIA_LEXICON: ReadonlySet<string> = new Set([
  // Streaming services & video platforms
  "netflix", "hbo", "hulu", "max", "peacock", "appletv", "showtime",
  "starz", "crunchyroll", "twitch", "youtube", "vimeo", "spotify", "pandora",
  // Gaming platforms / hardware
  "steam", "ps5", "ps4", "ps3", "ps2", "xbox", "switch", "wii", "gameboy",
  "nintendo", "playstation", "kindle", "oculus",
  // Iconic single-word game titles
  "zelda", "minecraft", "fortnite", "tetris", "stardew", "factorio",
  "tsushima", "stellaris", "skyrim", "fallout", "destiny", "valheim",
  "rimworld", "terraria", "hades", "celeste", "spelunky", "subnautica",
  "smash", "halo", "doom", "balatro", "civilization", "civ", "diablo",
  "warcraft", "starcraft", "overwatch", "valorant", "csgo", "pubg", "tarkov",
  "rust", "elden", "witcher", "cyberpunk", "borderlands", "darksouls",
  "bloodborne", "sekiro", "outerwilds", "satisfactory", "oxygen", "kerbal",
  "noita", "hollow", "dredge", "vampire", "deadcells", "risk", "monopoly",
  "scrabble", "catan", "wordle",
  // Show titles (single recognizable word)
  "severance", "succession", "andor", "yellowjackets", "ozark", "westworld",
  "loki", "wandavision", "mandalorian", "bridgerton", "wednesday", "stranger",
  // Reader devices
  "kobo",
]);

// Hard-coded set of 3-letter all-caps codes treated as airports.
// Anything 3-letter all-caps NOT in this set falls through to ACRONYM.
// Verbatim from `entity_normalizer.rs::airport_codes`.
const AIRPORT_CODES: ReadonlySet<string> = new Set([
  // Top US
  "SFO", "JFK", "LAX", "LHR", "EWR", "LGA", "ORD", "ATL", "BOS", "SEA",
  "DEN", "MIA", "DCA", "IAD", "DFW", "IAH", "PHX", "MSP", "MCO", "TPA",
  "PHL", "BWI", "PIT", "CLE", "DTW", "MKE", "STL", "BNA", "RDU", "RIC",
  "CMH", "IND", "CVG", "SDF", "MEM", "OAK", "SJC", "SAN", "PDX", "SLC",
  "AUS", "MSY", "MCI", "ABQ", "ELP", "TUS", "ONT", "SNA", "LGB", "BUR",
  "HNL", "OGG", "KOA", "PVD", "BUF", "ALB", "ROC", "BDL", "MHT", "SYR",
  "CAK", "GRR", "MSN", "CLT", "JAX",
  // Common intl
  "YYZ", "YVR", "YUL", "MEX", "GRU", "EZE", "SCL", "LIM", "BOG", "CDG",
  "ORY", "FRA", "MUC", "AMS", "MAD", "BCN", "FCO", "MXP", "ZRH", "VIE",
  "CPH", "ARN", "OSL", "HEL", "DUB", "BRU", "WAW", "PRG", "BUD", "IST",
  "ATH", "TLV", "DXB", "DOH", "AUH", "RUH", "BOM", "DEL", "BLR", "HKG",
  "PEK", "PVG", "CAN", "ICN", "GMP", "NRT", "HND", "KIX", "SIN", "BKK",
  "KUL", "CGK", "MNL", "SYD", "MEL", "BNE", "AKL", "JNB", "CAI", "ADD",
  "NBO", "LOS", "ACC",
]);

const CONNECTORS: ReadonlySet<string> = new Set([
  "w/", "with", "@", "&", "vs", "vs.", "->", "→", "+",
]);

const W_WITH: ReadonlySet<string> = new Set(["w/", "with", "w"]);

const DIGITS = /^[0-9]+$/;

/** Whitespace tokenization — mirrors Rust `str::split_whitespace`. */
function tokenize(s: string): string[] {
  return s.split(/\s+/u).filter((t) => t.length > 0);
}

// `7am`, `12pm`, `5:30`, `5:30am`, `9:00`.
function isTime(tok: string): boolean {
  const lc = asciiLowercase(tok);
  if (lc.endsWith("am") || lc.endsWith("pm")) {
    const rest = lc.slice(0, lc.length - 2);
    return isHhmm(rest) || isHh(rest);
  }
  return isHhmm(lc);
}

function isHh(s: string): boolean {
  if (s.length === 0 || s.length > 2) return false;
  return DIGITS.test(s);
}

function isHhmm(s: string): boolean {
  const idx = s.indexOf(":");
  if (idx < 0) return false;
  const h = s.slice(0, idx);
  const m = s.slice(idx + 1);
  return (
    h.length > 0 && h.length <= 2 && m.length === 2 &&
    DIGITS.test(h) && DIGITS.test(m)
  );
}

// `4-5`, `9:00-10:30`, `7-9am`.
function isTimeRange(tok: string): boolean {
  const lc = asciiLowercase(tok);
  let core = lc;
  if (lc.endsWith("am") || lc.endsWith("pm")) core = lc.slice(0, lc.length - 2);
  let idx = -1;
  for (let i = 0; i < core.length; i++) {
    const ch = core[i];
    if (ch === "-" || ch === "–") {
      idx = i;
      break;
    }
  }
  if (idx < 0) return false;
  const a = core.slice(0, idx);
  const b = core.slice(idx + 1);
  return (isHh(a) && isHh(b)) || (isHhmm(a) && isHhmm(b));
}

// `30min`, `2hr`, `2h`, `45m`-style minute counts.
function isDuration(tok: string): boolean {
  const lc = asciiLowercase(tok);
  for (const suffix of ["min", "hr", "hrs", "mins"]) {
    if (lc.endsWith(suffix)) {
      const num = lc.slice(0, lc.length - suffix.length);
      if (num.length > 0 && DIGITS.test(num)) return true;
    }
  }
  // bare `h` — only digit-prefixed
  if (lc.endsWith("h")) {
    const num = lc.slice(0, lc.length - 1);
    if (num.length > 0 && DIGITS.test(num)) return true;
  }
  return false;
}

// `5k`, `10k`, `2k`.
function isDistance(tok: string): boolean {
  const lc = asciiLowercase(tok);
  if (!lc.endsWith("k")) return false;
  const num = lc.slice(0, lc.length - 1);
  return num.length > 0 && DIGITS.test(num);
}

// 3-letter all-caps (airport-or-acronym).
function isCaps3(tok: string): boolean {
  return /^[A-Z]{3}$/.test(tok);
}

// CAPS_2 only — see Rust note: CAPS_3 handled separately, CAPS_LONG
// (4+) intentionally falls through to the lowercase plain path.
function isCapsAcronym(tok: string): boolean {
  return /^[A-Z]{2}$/.test(tok);
}

// `Sarah's`, `Barry's` — Title-case word + 's (straight or curly).
function isPersonPossessive(tok: string): boolean {
  let rest: Option<string> = null;
  if (tok.endsWith("'s")) rest = tok.slice(0, tok.length - 2);
  else if (tok.endsWith("’s")) rest = tok.slice(0, tok.length - 2);
  if (rest == null) return false;
  const cps = Array.from(rest);
  if (cps.length === 0) return false;
  const [head, ...tail] = cps;
  if (head == null || !/^[A-Z]$/.test(head)) return false;
  for (const cp of tail) {
    if (!/^[a-z]$/.test(cp)) return false;
  }
  return true;
}

function isPlainNum(tok: string): boolean {
  return DIGITS.test(tok);
}

// CamelCase (SoulCycle) or single capital word (Sarah).
function isCapWord(tok: string): boolean {
  const cps = Array.from(tok);
  if (cps.length === 0) return false;
  const [head, ...tail] = cps;
  if (head == null || !/^[A-Z]$/.test(head)) return false;
  for (const cp of tail) {
    if (!/^[A-Za-z]$/.test(cp)) return false;
  }
  return true;
}

// `home`, `office`, `mom` — lowercase ASCII + apostrophe/dash.
function isLowerWord(tok: string): boolean {
  if (tok.length === 0) return false;
  for (const c of tok) {
    if (!/^[a-z]$/.test(c) && c !== "'" && c !== "-") return false;
  }
  return true;
}

function isConnector(tok: string): boolean {
  return CONNECTORS.has(tok);
}

function isWWith(tok: string): boolean {
  return W_WITH.has(tok);
}

/**
 * Classify a raw token by shape. Returns `null` for plain tokens (the
 * caller lowercases and stores them as a plain token). Mirrors Rust
 * `classify_standalone` — the order of checks is load-bearing.
 */
function classifyStandalone(tok: string): Option<EntityTag> {
  if (isTime(tok)) return EntityTag.Time;
  if (isTimeRange(tok)) return EntityTag.TimeRange;
  if (isDuration(tok)) return EntityTag.Duration;
  if (isDistance(tok)) return EntityTag.Distance;
  if (isCaps3(tok)) {
    return AIRPORT_CODES.has(tok) ? EntityTag.Airport : EntityTag.Acronym;
  }
  if (isCapsAcronym(tok)) return EntityTag.Acronym;
  if (isPersonPossessive(tok)) return EntityTag.PersonPossessive;
  if (isPlainNum(tok)) return EntityTag.Num;
  return null;
}

/**
 * Normalize a title into a sequence of tokens, each either a plain
 * lowercased word or an entity tag. The priority order matches Rust
 * `normalize` exactly: media → after-w/with → after-@ → standalone
 * shape → connector → plain.
 */
export function normalize(title: string): Token[] {
  const raw = tokenize(title);
  const out: Token[] = [];
  let prev: Option<string> = null;

  for (const tok of raw) {
    // (0) Media lexicon — highest-priority content match.
    const lc = asciiLowercase(tok);
    if (MEDIA_LEXICON.has(lc)) {
      out.push({ kind: "entity", tag: EntityTag.Media });
      prev = tok;
      continue;
    }
    // (1) Token after w/with — always PERSON, regardless of case.
    if (prev !== null && isWWith(prev)) {
      out.push({ kind: "entity", tag: EntityTag.Person });
      prev = tok;
      continue;
    }
    // (2) Token after `@` — PLACE if word-shaped, but NOT if it's a
    // NUM/TIME (time anchor; leave it to standalone classify below).
    if (prev === "@") {
      const t = classifyStandalone(tok);
      if (t === EntityTag.Time || t === EntityTag.TimeRange || t === EntityTag.Num) {
        // time anchor — fall through to standalone classify
      } else if (isCapWord(tok) || isLowerWord(tok)) {
        out.push({ kind: "entity", tag: EntityTag.Place });
        prev = tok;
        continue;
      }
    }
    // (3) Standalone shape rules.
    const tag = classifyStandalone(tok);
    if (tag != null) {
      out.push({ kind: "entity", tag });
      prev = tok;
      continue;
    }
    // (4) Connectors keep their literal lowercase form.
    if (isConnector(tok)) {
      out.push({ kind: "plain", text: asciiLowercase(tok) });
      prev = tok;
      continue;
    }
    // (5) Default: plain lowercased token.
    out.push({ kind: "plain", text: asciiLowercase(tok) });
    prev = tok;
  }
  return out;
}
